"use client";

import type { FocusEvent, KeyboardEvent } from "react";
import { useState } from "react";

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/components/atoms/Command";
import { InfiniteScrollComponent } from "@/components/atoms/InfiniteScrollComponent";
import { Paragraph } from "@/components/atoms/Paragraph";
import {
  drownDownContainerId,
  searchedAppsContainerID,
} from "@/components/molecules/MainSearch/constants";
import FoundedAppsList from "@/components/molecules/MainSearch/foundedAppsList";
import MainSearchEmptyState from "@/components/molecules/MainSearch/mainSearchEmptyState";
import MainSearchLoading from "@/components/molecules/MainSearch/mainSearchLoading";
import PopularAppsSuggestionsList from "@/components/molecules/MainSearch/popularAppsSuggestionsList";
import { usePopularAppsForSearchSuggestionsQuery } from "@/hooks/apis/queries/usePopularAppsForSearchSuggestionsQuery";
import { useSearchApplicationsInfiniteQuery } from "@/hooks/apis/queries/useSearchApplicationsInfiniteQuery";
import { useAppRouter } from "@/hooks/common/useAppRouter";
import { useMediaQuery } from "@/hooks/common/useMediaQuery";
import { useTranslations } from "@/hooks/common/useTranslations";

import { SCREEN_SIZES } from "@/common/globalTypes/enums/common";

// eslint-disable-next-line complexity
const MainSearch = () => {
  const { t } = useTranslations("common");
  const { appPush } = useAppRouter();
  const isMobile = useMediaQuery(SCREEN_SIZES.sm);
  const {
    popularAppsForSearchSuggestions,
    isLoadingPopularAppsForSearchSuggestions,
  } = usePopularAppsForSearchSuggestionsQuery();

  const {
    foundedApps,
    searchTerm,
    onSearchTermHandler,
    isLoadingsFoundedApplications,
    hasNextPageSearchedApplications,
    fetchNextPageSearchedApplications,
  } = useSearchApplicationsInfiniteQuery();

  const isNotSearched = searchTerm.length === 0;

  const isLoadingFoundedApplications =
    isLoadingPopularAppsForSearchSuggestions || isLoadingsFoundedApplications;

  const isEmptyFoundedApps =
    !isLoadingFoundedApplications &&
    !(isNotSearched ? popularAppsForSearchSuggestions : foundedApps)?.length;

  const isAddHeadingFoundedApp = isNotSearched && {
    heading: (
      <Paragraph size="extraSmall" fontWeight="regular" type="secondary">
        {t("popularApps")}
      </Paragraph>
    ),
  };

  const [isOpen, setIsOpen] = useState(false);

  const onClickAppHandlerToSinglePage = (appAlias: string) => {
    appPush(`/${appAlias}`);
  };

  const onEnterSearchedValue = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      appPush(`/search?q=${searchTerm}`);
    }
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    if (
      !e.relatedTarget ||
      e.relatedTarget?.closest(`.${drownDownContainerId}`) === null
    ) {
      setIsOpen(false);
    }
  };

  return (
    <Command
      className={`max-w-lg rounded-xl relative overflow-visible ${drownDownContainerId}`}
      onClick={() => setIsOpen(true)}
    >
      <CommandInput
        placeholder="Search for an app"
        className="flex h-12 bg-background max-w-lg rounded-xl"
        onValueChange={onSearchTermHandler}
        onKeyDown={onEnterSearchedValue}
        onFocus={() => setIsOpen(true)}
        onBlur={onBlur}
        onClick={() => isMobile && appPush("/search")}
      />
      {isOpen && !isLoadingPopularAppsForSearchSuggestions && (
        <CommandList
          className="absolute top-full mb-2 max-h-60 w-full h-56 z-40 bg-background shadow-lg rounded-xl overflow-auto"
          id={searchedAppsContainerID}
        >
          {isLoadingFoundedApplications && <MainSearchLoading />}
          {isEmptyFoundedApps && <MainSearchEmptyState />}
          {
            <CommandGroup {...isAddHeadingFoundedApp} forceMount>
              {searchTerm.length === 0 ? (
                <PopularAppsSuggestionsList
                  popularAppsList={popularAppsForSearchSuggestions}
                  onClickHandler={onClickAppHandlerToSinglePage}
                />
              ) : (
                <InfiniteScrollComponent
                  hasMore={hasNextPageSearchedApplications}
                  loadMore={fetchNextPageSearchedApplications}
                  loader={<MainSearchLoading />}
                  target={searchedAppsContainerID}
                >
                  <FoundedAppsList
                    foundedApps={foundedApps}
                    onClickHandler={onClickAppHandlerToSinglePage}
                  />
                </InfiniteScrollComponent>
              )}
            </CommandGroup>
          }
        </CommandList>
      )}
    </Command>
  );
};

export default MainSearch;
